import {CustomText, H3Text, H4Text, H6Text} from '@buncha/components/core/text';
import {GestureResponderEvent, StyleSheet} from 'react-native';
import React, {Fragment, useCallback, useState} from 'react';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {BottomButton} from '@buncha/components/composites/Button';
import BottomSheet from '@buncha/components/composites/bottomSheet';
import {Box} from '@buncha/components/core/view';
import CustomInput from '@buncha/itemDetails/components/CustomItem/CustomInput';
import {Divider} from 'native-base';
import {EventType} from '@buncha/config/analytics/type';
import {FlatBarcode} from '@buncha/components/composites/Scanner/type';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {Touchable} from '@buncha/components/core/touchable';
import {navigation} from '@buncha/config/navigation';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useRoute} from '@react-navigation/native';

interface Props {
  setScanner: (val: boolean) => void;
  findItem: (barcode?: FlatBarcode, base64?: string, plu?: string) => void;
  addAnother: () => void;
  itemName?: string;
  price?: number;
}

const UnableToScan = (props: Props) => {
  const {setScanner, findItem, addAnother, itemName, price} = props;
  const theme = useColorThemeType('main');
  const mortise = useFontTheme('mortise');
  const muli = useFontTheme('muli');
  const route = useRoute();
  const {title, body, button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};
  const routeParams: any = route?.params;
  const runId = routeParams?.runId;
  const orderId = routeParams?.orderId;
  const storeId = routeParams?.storeId;
  const storeIndex = routeParams?.storeIndex;
  const initialPlu = routeParams?.plu;
  const isB2BStore = useIsMeijerStore(storeId);

  const [open, setOpen] = useState(false);
  const [plu, setPlu] = useState('');

  const toggleBottomSheet = useCallback(() => {
    setScanner(open);
    setOpen(!open);
  }, [open, setScanner]);

  const onPress = useCallback(
    (event: GestureResponderEvent) => {
      event?.stopPropagation();
      event?.preventDefault();
      Analytics.eventWithProps('unable to scan', {
        type: EventType.cta,
        source: 'Meijer Scanner Page',
        client: getClientType(isB2BStore),
      });
      toggleBottomSheet();
    },
    [isB2BStore, toggleBottomSheet],
  );

  const search = useCallback(() => {
    addAnother();
    findItem(undefined, undefined, plu);
    Analytics.eventWithProps('PLU Submit', {
      type: EventType.cta,
      runId: runId,
      pluNumber: plu,
      itemName: itemName,
      itemPrice: price,
      client: getClientType(isB2BStore),
      storeType: getStoreName(String(storeId)),
    });
    setOpen(false);
    setScanner(true);
    setPlu('');
  }, [
    addAnother,
    findItem,
    plu,
    runId,
    itemName,
    price,
    isB2BStore,
    storeId,
    setScanner,
  ]);

  const navigateToCatalogueAdd = useCallback(() => {
    toggleBottomSheet();
    Analytics.eventWithProps('Search Catalogue', {
      type: EventType.cta,
      runId: runId,
      client: getClientType(isB2BStore),
      storeType: getStoreName(String(storeId)),
      orderId,
      storeId,
    });
    Analytics.eventWithProps('Navigate to Add Item', {
      type: EventType.cta,
      runId: runId,
      client: getClientType(isB2BStore),
      storeType: getStoreName(String(storeId)),
      orderId,
      storeId,
    });
    navigation.navigate(ShopperScreens.AddItemPage, {
      storeId,
      runId,
      orderId,
      storeIndex,
    });
  }, [isB2BStore, orderId, runId, storeId, storeIndex, toggleBottomSheet]);

  return (
    <Fragment>
      <If condition={!initialPlu}>
        <Touchable
          style={[
            styles.unableToScanBox,
            {
              backgroundColor: theme?.background?.white?.[100],
            },
          ]}
          onPress={onPress}>
          <CustomText
            style={[
              styles.textUnderline,
              {
                fontFamily: muli?.[700]?.normal,
                color: theme?.text?.gray?.[100],
              },
            ]}>
            {body?.unableScan}
          </CustomText>
        </Touchable>
      </If>
      <BottomSheet open={open} onClose={toggleBottomSheet}>
        <Box style={styles.parentBox}>
          <Box style={styles.textContainer}>
            <H3Text
              style={[
                styles.textAlignLeft,
                {
                  fontFamily: mortise?.[700]?.normal,
                  color: theme?.text?.blue?.[300],
                },
              ]}>
              {body?.unableScanItem}
            </H3Text>
            <H6Text
              style={[
                styles.textAlignLeft,
                {
                  fontFamily: muli?.[700]?.normal,
                  color: theme?.text?.gray?.[100],
                },
              ]}>
              {body?.addPLU}
            </H6Text>
          </Box>

          <Box style={styles.inputContainer}>
            <H4Text
              style={[
                styles.textAlignLeft,
                {
                  fontFamily: muli?.[700]?.normal,
                  color: theme?.text?.blue?.[300],
                },
              ]}>
              {title?.searchItemPLU}
            </H4Text>

            <CustomInput
              value={plu}
              onTextChange={setPlu}
              placeHolder={body?.pluNumber}
              textAlignVertical={'center'}
              extraStyles={styles.input}
              keyboardType="number-pad"
            />
            <If condition={plu?.length}>
              <BottomButton
                style={styles.bottomButton}
                containerStyle={styles.bottomButtonContainer}
                title={button?.submit}
                onPress={search}
              />
            </If>
          </Box>
          <Divider />

          <BottomButton
            style={[styles.bottomButton]}
            containerStyle={styles.bottomButtonContainer}
            title={button?.searchCatalogue}
            borderColor={theme?.background?.green?.[100]}
            borderWidth={1}
            titleColor={theme?.background?.green?.[100]}
            bgColor={theme?.background?.white?.[100]}
            onPress={navigateToCatalogueAdd}
          />
        </Box>
      </BottomSheet>
    </Fragment>
  );
};

export default React.memo(UnableToScan);

const styles = StyleSheet.create({
  parentBox: {
    paddingVertical: 15,
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    paddingHorizontal: 15,
    gap: 20,
  },
  textAlignLeft: {textAlign: 'left'},
  textContainer: {
    gap: 10,
  },
  unableToScanBox: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    zIndex: 1,
    paddingVertical: 3,
    paddingHorizontal: 7,
    borderRadius: 5,
    gap: 5,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textUnderline: {
    textDecorationLine: 'underline',
  },
  bottomButton: {
    width: '100%',
  },
  bottomButtonContainer: {
    paddingBottom: 0,
    paddingTop: 0,
  },
  input: {height: 50},
  inputContainer: {width: '100%', gap: 10},
});
